var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { debounce, handleSizeChange, handleWindowResize, sizes, updateMeasurementPositions, } from '../../Editor/DecalEditorCanvasSizing';
import { useDispatch, useStore } from 'react-redux';
import { CANVAS_SELECTION_MODIFIED, CANVAS_TEXT_PROPERTY_CHANGED, LOAD_SIGN_FOR_EDIT_REQUEST, OBJECT_CHANGED, SHOW_PRODUCT_TEMPLATE_EDIT_MODAL, } from '../../Rutab/RutabConstants';
import { Canvas } from 'fabric';
import SelectedObjectMenu from '../Editor/SelectedObjectMenu';
import { clearGuidLines, handleGuideLines, handleSelectedMenuVisibility, } from '../../Editor/MovingObjectHelper';
import MenuOptions from '../Editor/MenuOptions';
import DecalEditorFooter from './DecalEditorFooter';
import { loadCartImages, loadDecalEditorSettings, loadEditorTemplate, loadSignForEdit, } from '../../Actions/Rutab/DecalEditor.action';
import { saveCanvasState, clearHistory, } from '../../Actions/Rutab/HistoryActions';
import { formTypes } from '../Editor/Interfaces';
import LoadingOverlay from '../Editor/LoadingOverlay';
import { BuildCanvasFromExistingSign } from '../../Editor/SignBuilderService';
import { useActiveObject, useCanvasReset, useLoadingSignForEdit, useProductTemplate, useSignForEdit, useSnappingEnabled, } from '../../Editor/Hooks/useReadOnlyState';
import { CART_SHOW_INFO } from '../../constants';
import { calculateFontSize } from '../../Editor/CanvasObjectService';
import EditorToolbar from '../Editor/EditorToolbar';
import Settings from '../Editor/Settings';
export default function DecalEditor() {
    const fabricCanvas = useRef(null);
    const fabricRef = useRef(null);
    const snappingEnabledRef = useRef(true);
    const [isCanvasReady, setCanvasToReadyState] = useState(false);
    const dispatch = useDispatch();
    const activeObject = useActiveObject();
    const canvasReset = useCanvasReset();
    const loadingSignForEdit = useLoadingSignForEdit();
    const signForEdit = useSignForEdit();
    const productTemplate = useProductTemplate();
    const snappingEnabled = useSnappingEnabled();
    const store = useStore();
    const debouncedSaveCanvasState = useCallback(debounce(() => {
        if (fabricCanvas.current) {
            dispatch(saveCanvasState(fabricCanvas.current));
        }
    }, 300), [dispatch]);
    useEffect(() => {
        dispatch(loadDecalEditorSettings());
        dispatch(loadCartImages());
    }, [dispatch]);
    useEffect(() => {
        snappingEnabledRef.current = snappingEnabled;
    }, [snappingEnabled]);
    useEffect(() => {
        if (fabricCanvas.current) {
            fabricCanvas.current.clear();
            fabricCanvas.current.backgroundColor = '#FFFFFF';
            fabricCanvas.current.clipPath = null;
            handleSizeChange({
                sizeKey: 'A5',
                width: sizes.A5.width,
                height: sizes.A5.height,
                orientation: 'portrait',
            }, fabricCanvas.current);
            updateMeasurementPositions(sizes.A5.height, sizes.A5.width, fabricRef.current);
            dispatch(clearHistory());
            dispatch(saveCanvasState(fabricCanvas.current));
        }
    }, [canvasReset]);
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const id = params.get('id');
        const templateId = params.get('templateId');
        if (id &&
            /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)) {
            dispatch(loadSignForEdit(id));
        }
        if (templateId) {
            dispatch(loadEditorTemplate(templateId));
        }
    }, [dispatch]);
    const applySignToEdit = useCallback((sign_1, ...args_1) => __awaiter(this, [sign_1, ...args_1], void 0, function* (sign, showInfo = true) {
        BuildCanvasFromExistingSign(sign, fabricCanvas.current, fabricRef.current, dispatch);
        setTimeout(() => {
            dispatch({
                type: LOAD_SIGN_FOR_EDIT_REQUEST,
                payload: {
                    isLoading: false,
                },
            });
            dispatch({
                type: CART_SHOW_INFO,
                payload: {
                    showInfo: showInfo,
                },
            });
        }, 400);
    }), [signForEdit]);
    useEffect(() => {
        if (signForEdit && fabricCanvas.current && isCanvasReady) {
            const sign = signForEdit;
            applySignToEdit(sign);
        }
    }, [signForEdit]);
    useEffect(() => {
        if (productTemplate &&
            fabricCanvas.current &&
            isCanvasReady &&
            !signForEdit) {
            const sign = productTemplate.template;
            applySignToEdit(sign, false);
        }
    }, [productTemplate]);
    useEffect(() => {
        if (fabricRef.current) {
            fabricCanvas.current = new Canvas(fabricRef.current, {
                width: 708.99,
                height: 1006,
                backgroundColor: '#FFFFFF',
                selection: false,
                preserveObjectStacking: true,
            });
        }
        fabricCanvas.current.on('text:editing:exited', (e) => {
            const state = store.getState();
            const prevFontSize = state.decalEditor.canvasTextProperty.fontSize;
            const prevText = state.decalEditor.canvasTextProperty.text;
            if (e.target.fontSize !== prevFontSize || e.target.text !== prevText) {
                dispatch({
                    type: CANVAS_TEXT_PROPERTY_CHANGED,
                    payload: {
                        canvasTextProperty: {
                            fontSize: e.target.fontSize,
                            text: e.target.text,
                        },
                    },
                });
            }
        });
        fabricCanvas.current.on('object:modified', (e) => {
            const state = store.getState();
            const isUndoRedoInProgress = state.decalEditor.isUndoRedoInProgress;
            if (isUndoRedoInProgress)
                return;
            if (snappingEnabledRef.current) {
                clearGuidLines(fabricCanvas.current);
            }
            dispatch({
                type: OBJECT_CHANGED,
                payload: { activeObjectLocationChanged: e.target.getBoundingRect() },
            });
            debouncedSaveCanvasState(fabricCanvas.current);
            const textObj = e.target;
            if (!textObj || textObj.type !== 'textbox')
                return;
            if (textObj.scaleX == 1)
                return;
            calculateFontSize(textObj);
            dispatch({
                type: CANVAS_TEXT_PROPERTY_CHANGED,
                payload: {
                    canvasTextProperty: {
                        fontSize: textObj.fontSize,
                        text: textObj.text,
                    },
                },
            });
        });
        fabricCanvas.current.on('object:added', (e) => {
            if (e.target.evented) {
                const state = store.getState();
                const isUndoRedoInProgress = state.decalEditor.isUndoRedoInProgress;
                if (isUndoRedoInProgress) {
                    return;
                }
            }
            fabricCanvas.current.getObjects().find((object) => {
                if (object.id === 'hole-group') {
                    fabricCanvas.current.bringObjectToFront(object);
                }
            });
            if (e.target.evented) {
                if (fabricCanvas.current) {
                    debouncedSaveCanvasState(fabricCanvas.current);
                }
            }
        });
        const debouncedHandleWindowResize = debounce(() => {
            handleWindowResize(fabricCanvas.current, fabricRef.current)(dispatch);
        }, 300);
        window.addEventListener('resize', (_) => {
            document.querySelectorAll('.measurement').forEach((element) => {
                element.classList.toggle('hidden', true);
            });
            debouncedHandleWindowResize();
        });
        fabricCanvas.current.on('object:moving', (e) => {
            handleSelectedMenuVisibility(true);
            if (snappingEnabledRef.current) {
                handleGuideLines(fabricCanvas.current, e.target);
            }
        });
        fabricCanvas.current.on('object:scaling', (e) => {
            handleSelectedMenuVisibility(true);
        });
        fabricCanvas.current.on('selection:created', (e) => {
            const isCanvasEditingLocked = store.getState().decalEditor
                .isEditingLocked;
            if (isCanvasEditingLocked) {
                dispatch({
                    type: SHOW_PRODUCT_TEMPLATE_EDIT_MODAL,
                    payload: {
                        showProductTemplateEditModal: true,
                    },
                });
                fabricCanvas.current.discardActiveObject();
                return;
            }
            dispatch({
                type: CANVAS_SELECTION_MODIFIED,
                payload: { activeSelection: e.selected },
            });
        });
        fabricCanvas.current.on('selection:updated', (e) => {
            const isCanvasEditingLocked = store.getState().decalEditor
                .isEditingLocked;
            if (isCanvasEditingLocked) {
                dispatch({
                    type: SHOW_PRODUCT_TEMPLATE_EDIT_MODAL,
                    payload: {
                        showProductTemplateEditModal: true,
                    },
                });
                fabricCanvas.current.discardActiveObject();
                return;
            }
            dispatch({
                type: CANVAS_SELECTION_MODIFIED,
                payload: { activeSelection: e.selected },
            });
        });
        fabricCanvas.current.on('selection:cleared', (e) => {
            dispatch({
                type: CANVAS_SELECTION_MODIFIED,
                payload: { activeSelection: [] },
            });
        });
        fabricCanvas.current.on('mouse:up', (obj) => {
            if (!obj.target)
                return;
            handleSelectedMenuVisibility(false);
        });
        //Footer fix. Decaledtor needs 100% height to work
        const mainContent = document.querySelector('.main-content');
        if (mainContent) {
            mainContent.style.height = '100%';
        }
        fabricCanvas.current.set({
            'form-id': formTypes.None,
            'hole-placement': 'none',
        });
        setCanvasToReadyState(true);
        return () => {
            fabricCanvas.current.dispose();
            fabricCanvas.current = null;
            window.removeEventListener('resize', debouncedHandleWindowResize);
        };
    }, []);
    useEffect(() => {
        if (isCanvasReady) {
            handleSizeChange({
                sizeKey: 'A5',
                width: sizes.A5.width,
                height: sizes.A5.height,
                orientation: 'portrait',
            }, fabricCanvas.current);
            updateMeasurementPositions(sizes.A5.height, sizes.A5.width, fabricRef.current);
            dispatch(clearHistory());
            if (fabricCanvas.current && !loadingSignForEdit) {
                dispatch(saveCanvasState(fabricCanvas.current));
            }
        }
    }, [isCanvasReady, dispatch]);
    return (_jsxs("div", { className: "page-wrapper-decal", children: [isCanvasReady && (_jsxs(_Fragment, { children: [_jsx(EditorToolbar, { canvas: fabricCanvas.current, canvasRef: fabricRef.current }), _jsx(Settings, { canvas: fabricCanvas.current, activeObject: activeObject })] })), _jsxs("div", { className: "menu-container", children: [isCanvasReady && (_jsx(MenuOptions, { canvas: fabricCanvas.current, canvasRef: fabricRef.current })), _jsxs("div", { className: "main-content-canvas", children: [_jsxs("div", { className: "canvas-wrapper", style: { position: 'relative' }, children: [_jsx("canvas", { ref: fabricRef, id: "canvas", style: {
                                            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                                        }, className: "canvas-box-shadow canvas-wrapper-guide" }), loadingSignForEdit && _jsx(LoadingOverlay, {})] }), _jsx("div", { className: "measurement", id: "measurementLeft" }), _jsx("div", { className: "measurement", id: "measurementBottom" }), activeObject.length > 0 && isCanvasReady && (_jsx(SelectedObjectMenu, { canvas: fabricCanvas.current, appSelector: activeObject }))] })] }), isCanvasReady && (_jsx(DecalEditorFooter, { fabricCanvas: fabricCanvas.current, isLoading: loadingSignForEdit }))] }));
}
